import type { ReactNode } from 'react';
import {
  Activity,
  AlertTriangle,
  Package,
  Pencil,
  ReceiptText,
  ShoppingCart,
  Tag,
  UserRound,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { formatDateTime, formatRelativeTime } from '@/lib/utils';
import type { HistoryEvent } from '@/lib/history';

interface HistoryTimelineProps {
  events: HistoryEvent[];
  isLoading?: boolean;
  emptyMessage?: string;
}

const TYPE_ICONS: Record<HistoryEvent['type'], ReactNode> = {
  activity: <Activity className="h-3.5 w-3.5 text-muted-foreground" />,
  customer: <UserRound className="h-3.5 w-3.5 text-violet-500" />,
  error: <AlertTriangle className="h-3.5 w-3.5 text-destructive" />,
  inventory: <Package className="h-3.5 w-3.5 text-amber-500" />,
  order: <ShoppingCart className="h-3.5 w-3.5 text-sky-500" />,
  order_line_item: <Tag className="h-3.5 w-3.5 text-sky-500" />,
  product: <Pencil className="h-3.5 w-3.5 text-muted-foreground" />,
  refund: <ReceiptText className="h-3.5 w-3.5 text-rose-500" />,
  variant: <Tag className="h-3.5 w-3.5 text-emerald-500" />,
};

export function HistoryTimeline({
  events,
  isLoading,
  emptyMessage = 'No activity recorded yet.',
}: HistoryTimelineProps) {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton key={index} className="h-14 w-full" />
        ))}
      </div>
    );
  }

  if (events.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyMessage}</p>;
  }

  return (
    <TooltipProvider delayDuration={200}>
      <ol>
        {events.map((event, index) => (
          <HistoryRow
            key={event.id}
            event={event}
            isLast={index === events.length - 1}
          />
        ))}
      </ol>
    </TooltipProvider>
  );
}

function HistoryRow({
  event,
  isLast,
}: {
  event: HistoryEvent;
  isLast: boolean;
}) {
  const content = (
    <div
      className="min-w-0 flex-1 pb-4 text-left"
      tabIndex={event.changes?.length ? 0 : undefined}
    >
      <div className="flex items-start gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          <Badge variant={event.status.variant} className="shrink-0 text-[10px]">
            {event.status.label}
          </Badge>
          <span className="truncate text-xs font-medium text-foreground">
            {event.title}
          </span>
        </div>
        <span
          className="shrink-0 text-[11px] text-muted-foreground"
          title={formatDateTime(event.timestamp)}
        >
          {formatRelativeTime(event.timestamp)}
        </span>
      </div>
      <p className="mt-0.5 text-[11px] text-muted-foreground">
        {event.attribution}
      </p>
    </div>
  );

  return (
    <li className="flex gap-3">
      <div className="flex flex-col items-center pt-0.5">
        <div className="shrink-0">{TYPE_ICONS[event.type]}</div>
        {!isLast && <div className="mt-1 w-px flex-1 bg-border" />}
      </div>

      {event.changes?.length ? (
        <Tooltip>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipContent
            side="left"
            align="start"
            sideOffset={12}
            className="max-h-80 w-96 max-w-[calc(100vw-2rem)] overflow-y-auto p-3"
          >
            <p className="mb-2 text-[11px] text-muted-foreground">
              {formatDateTime(event.timestamp)}
              {event.attributionTooltip
                ? ` \u00b7 ${event.attributionTooltip}`
                : ` \u00b7 ${event.attribution}`}
            </p>
            <div className="space-y-2">
              {event.changes.map((change) => (
                <div key={change.field} className="text-xs">
                  <p className="font-medium text-popover-foreground">
                    {change.field}
                  </p>
                  <p className="break-words text-muted-foreground">
                    <span className="line-through">{change.from}</span>
                    <span aria-hidden>{' \u2192 '}</span>
                    <span className="text-popover-foreground">{change.to}</span>
                  </p>
                </div>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
      ) : (
        content
      )}
    </li>
  );
}
